import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { trpc } from "../../lib/trpc.js";
import { baixar } from "../../lib/api.js";
import { Pagina } from "../../components/Pagina.js";
import { Sobreposicao } from "../../components/Sobreposicao.js";
import { Botao, Entrada, Falha, Girando, Selecao, Selo, Vazio, cx } from "../../components/ui.js";
import { DetalheCaptura } from "./DetalheCaptura.js";
import { MODOS_DO_ATENDIMENTO, ROTULO_DO_MODO, type ModoDoAtendimento } from "../geracao/etapas.js";

const POR_PAGINA = 24;

/**
 * O acervo, um card por foto tirada.
 *
 * Cada card é uma captura: a foto original e as imagens de IA que nasceram dela.
 * Antes a grade mostrava uma célula por geração, e duas imagens da mesma pessoa
 * apareciam como duas pessoas diferentes.
 *
 * O download em lote é por navegação direta — `fetch().blob()` carrega o ZIP
 * inteiro na memória do aparelho e mata a aba.
 */
export function BibliotecaPage() {
  /**
   * O lote vem pela URL, e não por um seletor.
   *
   * Quem chega filtrando por lote chega de um link da tela do lote — não é um
   * recorte que alguém monta do zero aqui. Na URL, o link é compartilhável e o
   * voltar do navegador desfaz o filtro; o número acompanha só para a etiqueta
   * ter o que dizer.
   */
  const [parametros, setParametros] = useSearchParams();
  const navegar = useNavigate();
  const loteId = parametros.get("lote") ?? "";
  const loteNumero = parametros.get("n");

  const [busca, setBusca] = useState("");
  const [instituicaoId, setInstituicaoId] = useState("");
  const [vivenciaId, setVivenciaId] = useState("");
  // Com IA ou só fotografia (#99); vazio, os dois.
  const [origem, setOrigem] = useState<ModoDoAtendimento | "">("");
  const [pagina, setPagina] = useState(0);
  const [selecionados, setSelecionados] = useState<number[]>([]);
  const [aberto, setAberto] = useState<string | null>(null);
  const [confirmandoDespublicar, setConfirmandoDespublicar] = useState(false);
  const [erroDespublicar, setErroDespublicar] = useState<string | null>(null);

  const instituicoes = trpc.institutions.list.useQuery({});
  // A lista de vivências acompanha a instituição escolhida: sem isso o operador
  // monta combinações que não existem e recebe uma tela vazia sem entender.
  const vivencias = trpc.vivencias.list.useQuery(
    instituicaoId ? { institutionId: instituicaoId } : undefined,
  );

  const lista = trpc.generations.listCapturas.useQuery(
    {
      search: busca || undefined,
      institutionId: instituicaoId || undefined,
      vivenciaId: vivenciaId || undefined,
      origem: origem || undefined,
      loteId: loteId || undefined,
      limit: POR_PAGINA,
      offset: pagina * POR_PAGINA,
    },
    {
      // Uma foto na fila do WhatsApp (#105) sai dela em segundos: sem reconsultar, o card diria "na fila" para sempre.
      refetchInterval: (q) =>
        q.state.data?.cards.some((c) => c.imagens.some((i) => i.envio.estado === "na_fila"))
          ? 5_000
          : false,
    },
  );

  const utils = trpc.useUtils();
  const despublicar = trpc.generations.despublicar.useMutation({
    onSuccess: async () => {
      setSelecionados([]);
      setConfirmandoDespublicar(false);
      await utils.generations.listCapturas.invalidate();
    },
    onError: (e) => setErroDespublicar(e.message),
  });
  // Tirar da TV, ou pôr de volta, sem despublicar (#96): sem confirmação, porque se desfaz no mesmo lugar.
  const mostrarNaGaleria = trpc.generations.mostrarNaGaleria.useMutation({
    onSuccess: async () => {
      setSelecionados([]);
      await utils.generations.listCapturas.invalidate();
    },
    onError: (e) => setErroDespublicar(e.message),
  });

  const cards = lista.data?.cards ?? [];
  const imagensSelecionadas = cards
    .flatMap((c) => c.imagens)
    .filter((i) => selecionados.includes(i.id));
  // Cada botão conta só o que ele muda: "Tirar da galeria 2" com uma já fora mudaria uma só.
  const idsNaGaleria = imagensSelecionadas.filter((i) => i.naGaleria).map((i) => i.id);
  const idsForaDaGaleria = imagensSelecionadas.filter((i) => !i.naGaleria).map((i) => i.id);
  const total = lista.data?.total ?? 0;
  const paginas = Math.ceil(total / POR_PAGINA);
  const cardAberto = cards.find((c) => c.capturaId === aberto);
  const filtrando = Boolean(busca || instituicaoId || vivenciaId || origem || loteId);

  /** Trocar um filtro invalida a página e a seleção que estava em curso. */
  function filtrar(mudanca: () => void) {
    mudanca();
    setPagina(0);
    setSelecionados([]);
  }

  return (
    <Pagina
      titulo="Biblioteca"
      descricao={`${total} ${total === 1 ? "foto" : "fotos"} no acervo.`}
      acao={
        selecionados.length > 0 ? (
          <div className="flex flex-wrap items-center justify-end gap-2">
            {confirmandoDespublicar ? (
              <>
                <span className="basis-full text-right text-[0.8125rem] text-texto-suave">
                  Despublicar {selecionados.length}? Saem também da impressão e do envio; o arquivo
                  fica.
                </span>
                <Botao
                  tom="descartar"
                  carregando={despublicar.isPending}
                  onClick={() => despublicar.mutate({ ids: selecionados })}
                >
                  Despublicar
                </Botao>
                <Botao
                  tom="quieto"
                  disabled={despublicar.isPending}
                  onClick={() => setConfirmandoDespublicar(false)}
                >
                  Cancelar
                </Botao>
              </>
            ) : (
              <>
                {idsNaGaleria.length > 0 && (
                  <Botao
                    tom="quieto"
                    carregando={mostrarNaGaleria.isPending}
                    onClick={() => {
                      setErroDespublicar(null);
                      mostrarNaGaleria.mutate({ ids: idsNaGaleria, mostrar: false });
                    }}
                  >
                    Tirar da galeria {idsNaGaleria.length}
                  </Botao>
                )}
                {idsForaDaGaleria.length > 0 && (
                  <Botao
                    tom="quieto"
                    carregando={mostrarNaGaleria.isPending}
                    onClick={() => {
                      setErroDespublicar(null);
                      mostrarNaGaleria.mutate({ ids: idsForaDaGaleria, mostrar: true });
                    }}
                  >
                    Pôr na galeria {idsForaDaGaleria.length}
                  </Botao>
                )}
                <Botao
                  tom="quieto"
                  onClick={() => {
                    setErroDespublicar(null);
                    setConfirmandoDespublicar(true);
                  }}
                >
                  Despublicar {selecionados.length}
                </Botao>
                <Botao
                  tom="quieto"
                  onClick={() => navegar(`/impressao?fotos=${selecionados.join(",")}`)}
                >
                  Imprimir {selecionados.length}
                </Botao>
                <Botao
                  tom="brasa"
                  onClick={() => baixar(`/api/generations/export?ids=${selecionados.join(",")}`)}
                >
                  Baixar {selecionados.length}
                </Botao>
              </>
            )}
          </div>
        ) : undefined
      }
    >
      {erroDespublicar && (
        <div className="mb-4">
          <Falha mensagem={erroDespublicar} />
        </div>
      )}
      {/* No celular a busca ocupa a linha, Local e vivência dividem a de baixo, e o tipo vem por último. */}
      <div className="mb-4 grid grid-cols-2 gap-2 sm:grid-cols-4">
        <Entrada
          className="col-span-2 sm:col-span-1"
          type="search"
          placeholder="Nome ou telefone"
          aria-label="Buscar por nome ou telefone"
          value={busca}
          onChange={(e) => filtrar(() => setBusca(e.target.value))}
        />
        <Selecao
          className="truncate"
          aria-label="Local"
          value={instituicaoId}
          onChange={(e) =>
            filtrar(() => {
              setInstituicaoId(e.target.value);
              setVivenciaId("");
            })
          }
        >
          <option value="">Todos os locais</option>
          {instituicoes.data?.map((i) => (
            <option key={i.id} value={i.id}>
              {i.name}
            </option>
          ))}
        </Selecao>
        <Selecao
          className="truncate"
          aria-label="Vivência"
          value={vivenciaId}
          onChange={(e) => filtrar(() => setVivenciaId(e.target.value))}
        >
          <option value="">Todas as vivências</option>
          {vivencias.data?.map((v) => (
            <option key={v.id} value={v.id}>
              {v.name}
            </option>
          ))}
        </Selecao>
        <Selecao
          className="col-span-2 truncate sm:col-span-1"
          aria-label="Tipo de foto"
          value={origem}
          onChange={(e) => filtrar(() => setOrigem(e.target.value as ModoDoAtendimento | ""))}
        >
          <option value="">Todos os tipos</option>
          {MODOS_DO_ATENDIMENTO.map((m) => (
            <option key={m} value={m}>
              {ROTULO_DO_MODO[m]}
            </option>
          ))}
        </Selecao>
      </div>

      {loteId !== "" && (
        <div className="mb-4 flex items-center gap-2">
          <Selo tom="brasa">{loteNumero ? `Lote #${loteNumero}` : "Um lote"}</Selo>
          <Botao
            tom="fantasma"
            formato="nu"
            onClick={() =>
              filtrar(() => {
                const proximos = new URLSearchParams(parametros);
                proximos.delete("lote");
                proximos.delete("n");
                setParametros(proximos, { replace: true });
              })
            }
          >
            Ver o acervo inteiro
          </Botao>
        </div>
      )}

      {lista.isLoading ? (
        <div className="flex justify-center py-12">
          <Girando className="size-6 text-texto-fraco" />
        </div>
      ) : cards.length === 0 ? (
        <Vazio
          titulo={filtrando ? "Nada com esses filtros" : "Nenhuma foto ainda"}
          descricao={
            filtrando
              ? "Tente outra grafia, ou limpe os filtros."
              : "As fotos aparecem aqui assim que o primeiro atendimento é concluído."
          }
        />
      ) : (
        <>
          <ul className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
            {cards.map((c) => {
              const publicadas = c.imagens.filter((i) => i.publicada);
              const ativo = publicadas.some((i) => selecionados.includes(i.id));
              // "Na galeria" é o que a TV mostra: publicada e com a marca ligada (#96).
              const naGaleria = publicadas.some((i) => i.naGaleria);
              const soFotografia = c.imagens.some((i) => i.origem === "fotografia");

              return (
                <li key={c.capturaId} className="flex flex-col gap-2">
                  <button
                    onClick={() => setAberto(c.capturaId)}
                    className={cx(
                      "relative block aspect-[9/16] w-full overflow-hidden rounded-peca border-2",
                      "border-borda transition-colors hover:border-borda-forte",
                    )}
                  >
                    <img
                      src={c.capaMini}
                      alt={c.spectatorName}
                      loading="lazy"
                      className="size-full object-cover"
                    />

                    <span className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/90 to-transparent px-2 pb-2 pt-6 text-left">
                      <span className="block truncate text-[0.8125rem] font-medium text-texto">
                        {c.spectatorName}
                      </span>
                      <span className="mt-1 flex flex-wrap items-center gap-1">
                        {naGaleria ? (
                          <Selo tom="fixado">na galeria</Selo>
                        ) : publicadas.length > 0 ? (
                          <Selo tom="quieto">fora da galeria</Selo>
                        ) : c.capaEhFotoOriginal ? (
                          <Selo tom="quieto">sem imagem</Selo>
                        ) : (
                          <Selo tom="quieto">rascunho</Selo>
                        )}
                        {soFotografia && <Selo tom="quieto">só fotografia</Selo>}
                        {c.imagens.length > 1 && (
                          <Selo tom="quieto">{c.imagens.length} imagens</Selo>
                        )}
                      </span>
                    </span>
                  </button>

                  {publicadas.length > 0 && (
                    <label className="flex cursor-pointer items-center gap-2 text-[0.8125rem] text-texto-suave">
                      <input
                        type="checkbox"
                        checked={ativo}
                        onChange={() =>
                          setSelecionados((s) => {
                            const ids = publicadas.map((i) => i.id);
                            return ativo
                              ? s.filter((x) => !ids.includes(x))
                              : [...s, ...ids.filter((x) => !s.includes(x))];
                          })
                        }
                        className="size-4 accent-brasa"
                      />
                      Selecionar
                    </label>
                  )}
                </li>
              );
            })}
          </ul>

          {paginas > 1 && (
            <div className="mt-6 flex items-center justify-between">
              <Botao tom="quieto" disabled={pagina === 0} onClick={() => setPagina((p) => p - 1)}>
                Anterior
              </Botao>
              <span className="text-[0.8125rem] text-texto-fraco">
                {pagina + 1} de {paginas}
              </span>
              <Botao
                tom="quieto"
                disabled={pagina + 1 >= paginas}
                onClick={() => setPagina((p) => p + 1)}
              >
                Próxima
              </Botao>
            </div>
          )}
        </>
      )}

      {cardAberto && (
        <Sobreposicao
          rotulo={`Fotos de ${cardAberto.spectatorName}`}
          aoFechar={() => setAberto(null)}
        >
          <DetalheCaptura card={cardAberto} aoFechar={() => setAberto(null)} />
        </Sobreposicao>
      )}
    </Pagina>
  );
}
